import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { BASE_URL } from "../API/Constants";

const safe = (v, fb = "-") => (v === null || v === undefined || v === "" ? fb : v);
const toInt = (v, fb = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : fb;
};

export default function Category() {
  const nav = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const IMAGE_ORIGIN = useMemo(() => {
    const b = (BASE_URL || "").trim().replace(/\/+$/, "");
    return b.replace(/\/api$/i, "");
  }, []);

  const imgUrl = (path) => {
    if (!path) return "";
    const p = String(path).trim();
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    if (p.startsWith("/")) return `${IMAGE_ORIGIN}${p}`;
    return `${IMAGE_ORIGIN}/${p}`;
  };

  const pickList = (data, keys = []) => {
    if (Array.isArray(data)) return data;
    for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
    return [];
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await axiosInstance.get("/api/categories");
        const list = pickList(res.data, ["categories", "data", "items"]);

        // SORT BY sortOrder then name then id
        const sorted = [...(list || [])].sort((a, b) => {
          const ao = toInt(a?.sortOrder, 999999);
          const bo = toInt(b?.sortOrder, 999999);
          if (ao !== bo) return ao - bo;

          const an = String(a?.name || "").toLowerCase();
          const bn = String(b?.name || "").toLowerCase();
          if (an < bn) return -1;
          if (an > bn) return 1;

          return toInt(a?.id, 0) - toInt(b?.id, 0);
        });

        if (!mounted) return;
        setCategories(sorted);
      } catch (e) {
        if (!mounted) return;
        setErr(
          e?.response?.data?.msg ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load categories"
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => (mounted = false);
  }, []);

  return (
    <div className="catPage">
      <style>{css}</style>

      <div className="catWrap">
        {/* Title */}
        <div className="catHead">
          <div className="catTitle">Shop by Category</div>
          <div className="catLine" />
        </div>

        {err ? <div className="catMsg catErr">{err}</div> : null}

        {loading ? (
          <div className="catGrid skelGrid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="catCardSkel" key={i} />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="catMsg">No categories found</div>
        ) : (
          <div className="catGrid">
            {categories.map((c) => (
              <button
                key={c?.id}
                type="button"
                className="catCard"
                title={safe(c?.name)}
                onClick={() => nav(`/subcategories/${c.id}`, { state: { category: c } })}
              >
                <div className="catImgBg">
                  <img
                    src={imgUrl(c?.image) || "https://via.placeholder.com/140?text=C"}
                    alt={safe(c?.name)}
                    loading="lazy"
                    onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/140?text=C")}
                  />
                  {/* Subtle curve separator */}
                  <div className="catCurve"></div>
                </div>
                <div className="catName">{safe(c?.name)}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');

  *{ box-sizing:border-box; }

  .catPage {
    width: 100%;
    background: #fff;
    padding: 16px 0 20px;
    font-family: 'Montserrat', sans-serif;
  }

  .catWrap {
    width: min(1400px, 100%);
    margin: 0 auto;
    padding: 0 14px;
  }

  .catHead {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  .catTitle {
    font-size: 14px;
    font-weight: 700;
    color: #222;
    white-space: nowrap;
    letter-spacing: -0.3px;
  }
  .catLine {
    height: 1.5px;
    flex: 1;
    background: linear-gradient(90deg, rgba(0,0,0,.10), rgba(0,0,0,0));
  }

  .catMsg {
    margin: 10px 0;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid rgba(15,23,42,.08);
    background: #fff;
    font-weight: 700;
    color: rgba(15,23,42,.75);
  }
  .catErr {
    border-color: rgba(239,68,68,.25);
    background: rgba(239,68,68,.06);
    color: #7f1d1d;
  }

  /* ✅ GRID LAYOUT - 4 COLUMNS DEFAULT ON MOBILE */
  .catGrid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px 10px; /* row gap 12px, col gap 10px */
  }

  .catCard {
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;
    border: 1px solid rgba(0,0,0,0.04);
    box-shadow: 0 3px 10px rgba(0,0,0,0.06);
    overflow: hidden;
    cursor: pointer;
    text-align: center;
    padding: 0;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    height: 100%;
    font-family: 'Montserrat', sans-serif;
  }

  .catCard:hover, .catCard:focus {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
    outline: none;
  }

  .catImgBg {
    position: relative;
    width: 100%;
    aspect-ratio: 1.05 / 1;
    background: radial-gradient(circle at center, #f8fcfa 0%, #e6f2e8 100%);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
  }

  .catImgBg img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: relative;
    z-index: 2;
  }

  /* Curve effect at the bottom of the image area */
  .catCurve {
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 14px;
    background: #fff;
    border-top-left-radius: 50% 100%;
    border-top-right-radius: 50% 100%;
    z-index: 3;
    box-shadow: 0 -2px 6px rgba(76, 175, 80, 0.05); /* Adds soft depth matching the image */
  }

  .catName {
    background: #fff;
    padding: 4px 4px 10px;
    font-size: 9px;
    font-weight: 700;
    color: #222;
    line-height: 1.25;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex: 1;
  }

  /* ✅ Skeletons */
  .catCardSkel {
    aspect-ratio: 0.85;
    border-radius: 12px;
    background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
    background-size: 200% 100%;
    animation: skel 1.4s infinite linear;
  }
  @keyframes skel {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  /* MEDIA QUERIES FOR LARGER SCREENS */
  @media (min-width: 600px) {
    .catGrid { grid-template-columns: repeat(6, 1fr); gap: 16px; }
    .catName { font-size: 13px; padding: 6px 8px 16px; border-bottom-left-radius: 12px; border-bottom-right-radius: 12px; }
    .catImgBg { padding: 8px; border-top-left-radius: 12px; border-top-right-radius: 12px; }
  }
  @media (min-width: 1000px) {
    .catGrid { grid-template-columns: repeat(8, 1fr); gap: 20px; }
    .catTitle { font-size: 22px; }
  }
`;